import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getStudentFiles, updateStudentFile, deleteStudentFile, bulkDeleteStudentFiles, triggerFileDownload, triggerBulkFileDownload } from '../../../services/fileCabinetService';
import Loader from '../../common/Loader';
import Alert from '../../common/Alert';
import ConfirmModal from '../../common/ConfirmModal';
import DocumentViewer from '../../common/DocumentViewer';
import ListFileCabinet from './ListFileCabinet';
import EditFileCabinet from './EditFileCabinet';

const FILE_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'IEP', label: 'IEP' },
  { value: 'evaluation', label: 'Evaluation' },
  { value: 'progress_report', label: 'Progress Report' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'other', label: 'Other' },
];

export default function ViewFileCabinet() {
  const { id: studentId } = useParams();

  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, total_pages: 0 });

  const [filterType, setFilterType] = useState('');
  const [search, setSearch] = useState('');
  const debounceRef = useRef(null);

  const [viewFile, setViewFile] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteFile, setDeleteFile] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  const fetchFiles = useCallback(async (page = 1, fileType = filterType) => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: pagination.limit };
      if (fileType) params.file_type = fileType;
      const { data } = await getStudentFiles(studentId, params);
      setFiles(data.data?.files || []);
      setPagination({
        total: data.data?.total || 0,
        page: data.data?.page || 1,
        limit: data.data?.limit || 20,
        total_pages: data.data?.total_pages || 0,
      });
    } catch {
      setError('Failed to load files.');
    } finally {
      setLoading(false);
    }
  }, [studentId, filterType, pagination.limit]);

  useEffect(() => { fetchFiles(); }, []); // eslint-disable-line

  const handleTypeFilter = (value) => {
    setFilterType(value);
    setSelectedIds([]);
    fetchFiles(1, value);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchFiles(1, filterType);
    }, 400);
  };

  const clearFilters = () => {
    setFilterType('');
    setSearch('');
    setSelectedIds([]);
    fetchFiles(1, '');
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    fetchFiles(page, filterType);
  };

  const handleView = (file) => setViewFile(file);
  const handleEdit = (file) => setEditFile(file);
  const handleDeleteRequest = (file) => setDeleteFile(file);

  const handleDownload = async (file) => {
    try {
      await triggerFileDownload(studentId, file.id, file.file_name);
    } catch {
      setAlert({ type: 'error', message: 'Failed to download file.' });
    }
  };

  const handleEditSave = async (payload) => {
    if (!editFile) return;
    setEditLoading(true);
    try {
      await updateStudentFile(studentId, editFile.id, payload);
      setAlert({ type: 'success', message: 'File details updated successfully.' });
      setEditFile(null);
      fetchFiles(pagination.page, filterType);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to update file.';
      setAlert({ type: 'error', message: msg });
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteFile) return;
    setDeleting(true);
    try {
      await deleteStudentFile(studentId, deleteFile.id);
      setAlert({ type: 'success', message: 'File deleted successfully.' });
      setDeleteFile(null);
      fetchFiles(pagination.page, filterType);
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete file.' });
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      if (selectedIds.length === 1) {
        await deleteStudentFile(studentId, selectedIds[0]);
      } else {
        await bulkDeleteStudentFiles(studentId, selectedIds);
      }
      setAlert({ type: 'success', message: `${selectedIds.length} file${selectedIds.length > 1 ? 's' : ''} deleted successfully.` });
      setBulkDeleteConfirm(false);
      setSelectedIds([]);
      fetchFiles(pagination.page, filterType);
    } catch {
      setAlert({ type: 'error', message: 'Failed to delete files.' });
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.length === 0 || bulkDownloading) return;
    setBulkDownloading(true);
    try {
      if (selectedIds.length === 1) {
        const file = files.find((f) => f.id === selectedIds[0]);
        await triggerFileDownload(studentId, selectedIds[0], file?.file_name);
      } else {
        await triggerBulkFileDownload(studentId, selectedIds, 'student.zip');
      }
    } catch {
      setAlert({ type: 'error', message: 'Failed to download files.' });
    } finally {
      setBulkDownloading(false);
    }
  };

  const filteredFiles = search
    ? files.filter((f) => f.file_name?.toLowerCase().includes(search.toLowerCase()))
    : files;

  const hasActiveFilters = filterType || search;

  return (
    <div className="space-y-5">
      {alert && <Alert type={alert.type} message={alert.message} onClose={() => setAlert(null)} />}

      <ConfirmModal
        open={!!deleteFile}
        title="Delete File"
        message={`Are you sure you want to delete "${deleteFile?.file_name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteFile(null)}
        loading={deleting}
        variant="danger"
      />

      <ConfirmModal
        open={bulkDeleteConfirm}
        title="Delete Selected Files"
        message={`Are you sure you want to delete ${selectedIds.length} file${selectedIds.length > 1 ? 's' : ''}? This action cannot be undone.`}
        confirmText={`Delete ${selectedIds.length} File${selectedIds.length > 1 ? 's' : ''}`}
        onConfirm={handleBulkDeleteConfirm}
        onCancel={() => setBulkDeleteConfirm(false)}
        loading={bulkDeleting}
        variant="danger"
      />

      <EditFileCabinet
        open={!!editFile}
        file={editFile}
        onSave={handleEditSave}
        onCancel={() => setEditFile(null)}
        loading={editLoading}
      />

      <DocumentViewer
        open={!!viewFile}
        file={viewFile}
        studentId={studentId}
        onClose={() => setViewFile(null)}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link
            to={`/students/${studentId}`}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">File Cabinet</h1>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">
              Manage student documents and files
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          {selectedIds.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleBulkDownload}
                disabled={bulkDownloading}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-700 hover:bg-slate-800 disabled:opacity-60 text-white text-sm font-semibold transition-colors shadow-lg shadow-slate-500/20"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                {bulkDownloading ? 'Downloading…' : `Download (${selectedIds.length})`}
              </button>
              <button
                type="button"
                onClick={() => setBulkDeleteConfirm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-red-500/20"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Delete ({selectedIds.length})
              </button>
            </>
          )}
          <Link
            to={`/students/${studentId}/files/add`}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors shadow-lg shadow-indigo-500/20"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
            </svg>
            Upload Files
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search files..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-9 pr-3.5 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 text-sm transition-colors"
          />
        </div>

        <select
          value={filterType}
          onChange={(e) => handleTypeFilter(e.target.value)}
          className="px-3 py-2.5 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-colors"
        >
          {FILE_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="flex items-center justify-center w-9 h-9 rounded-xl border border-gray-200 dark:border-slate-700 text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:text-red-400 dark:hover:border-red-500/40 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
            title="Clear all filters"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* File list */}
      <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-2xl overflow-visible">
        {error ? (
          <div className="px-6 py-12 text-center text-red-500 text-sm">{error}</div>
        ) : loading ? (
          <Loader text="Loading files..." />
        ) : (
          <ListFileCabinet
            files={filteredFiles}
            loading={loading}
            onView={handleView}
            onEdit={handleEdit}
            onDelete={handleDeleteRequest}
            onDownload={handleDownload}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
          />
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && pagination.total_pages > 1 && (
        <div className="bg-white dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700 rounded-2xl px-5 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Showing{' '}
            <span className="font-medium text-gray-700 dark:text-slate-300">
              {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)}
            </span>{' '}
            of <span className="font-medium text-gray-700 dark:text-slate-300">{pagination.total}</span>
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              let page;
              if (pagination.total_pages <= 5) page = i + 1;
              else if (pagination.page <= 3) page = i + 1;
              else if (pagination.page >= pagination.total_pages - 2) page = pagination.total_pages - 4 + i;
              else page = pagination.page - 2 + i;
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors ${
                    page === pagination.page
                      ? 'bg-indigo-600 border-indigo-600 text-white shadow-sm'
                      : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.total_pages}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
